import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { ReactComponent as PrinterIcon } from '../../../assets/SVG/printer.svg';
import {
  useInvoicePaymentsQuery,
  fetchReceiptDetails,
  Receipt,
} from '../../../pages/Billing/Invoice/invoiceHelper';
import useCurrency from '../../CurrencyProvider';
import { formatDateShortMonth } from '../../../utils/dateUtils';
import { TABLE_HEADER_ROW_SX } from '../../../utils/typographyStyles';
import { ReceiptReportGen } from '../../Reports/Receipt';

export interface ReceiptDialogProps {
  open: boolean;
  onClose: () => void;
  invoiceId: number;
  billNo?: string | null;
}

const HEADER_CELL_KEYS = [
  { key: 'no', i18nKey: 'invoice.receipt.receipt_no', minWidth: 180 },
  { key: 'amount', i18nKey: 'invoice.receipt.receipt_amount', minWidth: 140 },
  { key: 'date', i18nKey: 'invoice.receipt.receipt_date', minWidth: 140 },
  { key: 'actions', i18nKey: 'invoice.receipt.actions', align: 'center' as const, minWidth: 80 },
];

const COMMON_CELL_SX = {
  verticalAlign: 'middle' as const,
  px: 2.5,
  py: 1.5,
};

const ReceiptDialog: React.FC<ReceiptDialogProps> = ({
  open,
  onClose,
  invoiceId,
  billNo,
}) => {
  const { formatCurrency } = useCurrency();
  const { t: tBilling } = useTranslation('billing_config');
  const [printingId, setPrintingId] = useState<number | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [receiptData, setReceiptData] = useState<Receipt | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState<string | null>(null);

  const { data, isLoading, isError } = useInvoicePaymentsQuery({
    invoiceId,
    enabled: open && invoiceId > 0,
  });

  const payments = data?.payments ?? [];
  const hasRows = payments.length > 0;

  const handlePrintReceipt = async (paymentId: number) => {
    setPrintingId(paymentId);
    setReceiptLoading(true);
    setReceiptError(null);
    setReportOpen(true);

    try {
      const response = await fetchReceiptDetails(paymentId);
      if (response.success && response.receipt) {
        setReceiptData(response.receipt);
      } else {
        setReceiptError(tBilling('invoice.receipt.error_details'));
      }
    } catch (error) {
      setReceiptError(tBilling('invoice.receipt.error_details'));
      // eslint-disable-next-line no-console
      console.error('Failed to fetch receipt details:', error);
    } finally {
      setPrintingId(null);
      setReceiptLoading(false);
    }
  };

  const handleReportClose = () => {
    setReportOpen(false);
    setReceiptData(null);
    setReceiptError(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        data-testid="receipt-dialog-modal"
        aria-labelledby="receipt-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 0,
          },
        }}
      >
        {/* Title bar */}
        <DialogTitle
          sx={{
            px: 3,
            pt: 2.5,
            pb: 1.5,
            borderBottom: '1px solid #E5E7EB',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography id="receipt-dialog-title" variant="h6" sx={{ fontWeight: 600 }}>
                {tBilling('invoice.receipt.title')}
              </Typography>
              {billNo && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                ({billNo})
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={onClose}
              aria-label={tBilling('invoice.receipt.aria.close_dialog')}
              data-testid="receipt-dialog-close"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Content: Table */}
        <DialogContent sx={{ px: 0, pt: 0, pb: 0 }}>
          <Box sx={{ px: 3, py: 2 }}>
            {isLoading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress size={32} />
              </Box>
            ) : isError ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'error.main' }}>
                  {tBilling('invoice.receipt.error')}
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table
                  size="small"
                  aria-label={tBilling('invoice.receipt.aria.receipt_table')}
                  data-testid="receipt-dialog-table"
                  sx={{
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    '& th, & td': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow sx={TABLE_HEADER_ROW_SX}>
                      {HEADER_CELL_KEYS.map((h, index) => (
                        <TableCell
                          key={h.key}
                          align={h.align ?? 'left'}
                          sx={{
                            ...(h.minWidth ? { minWidth: h.minWidth } : {}),
                            whiteSpace: 'nowrap',
                            px: 2.5,
                            ...(index === 0
                              ? {
                                borderTopLeftRadius: 12,
                                borderBottomLeftRadius: 12,
                              }
                              : {}),
                            ...(index === HEADER_CELL_KEYS.length - 1
                              ? {
                                borderTopRightRadius: 12,
                                borderBottomRightRadius: 12,
                              }
                              : {}),
                          }}
                        >
                          {tBilling(h.i18nKey)}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hasRows ? (
                      payments.map((payment) => (
                        <TableRow
                          key={payment.id}
                          hover
                          sx={{
                            '&:last-child td': { borderBottom: 0 },
                          }}
                        >
                          <TableCell sx={COMMON_CELL_SX}>
                            <Typography variant="body2">
                              {payment.receipt_no}
                            </Typography>
                          </TableCell>
                          <TableCell sx={COMMON_CELL_SX}>
                            <Typography variant="body2">
                              {formatCurrency(parseFloat(payment.bill_amount))}
                            </Typography>
                          </TableCell>
                          <TableCell sx={COMMON_CELL_SX}>
                            <Typography variant="body2">
                              {formatDateShortMonth(payment.transaction_date)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={COMMON_CELL_SX} align="center">
                            <IconButton
                              size="small"
                              aria-label={tBilling('invoice.receipt.aria.print_receipt', { receiptNo: payment.receipt_no })}
                              data-testid={`receipt-dialog-print-${payment.id}`}
                              sx={{ p: 0.5 }}
                              onClick={() => handlePrintReceipt(payment.id)}
                              disabled={printingId === payment.id}
                            >
                              {printingId === payment.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PrinterIcon />
                              )}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={HEADER_CELL_KEYS.length}
                          sx={{ py: 4, textAlign: 'center' }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary' }}
                          >
                            {tBilling('invoice.receipt.no_receipts')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid #E5E7EB',
          }}
        >
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={onClose}
              data-testid="receipt-dialog-cancel"
              sx={{ px: 4, borderRadius: 999 }}
            >
              {tBilling('invoice.receipt.cancel')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Receipt Print Report Dialog */}
      <ReceiptReportGen
        open={reportOpen}
        onClose={handleReportClose}
        data={receiptData}
        isLoading={receiptLoading}
        error={receiptError}
      />
    </>
  );
};

export default ReceiptDialog;